// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import translate from "@insite/client-framework/Translate";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import BarcodeScanner from "@werner/App/Components/ProductBarcodeScanner/BarcodeScanner";
import Barcode from "@werner/XDL/Icons/Barcode";
import { color, mergeWithCss } from "@werner/XDL/Utilities";
import debounce from "lodash/debounce";
import React, { useState } from "react";
import { css } from "styled-components";

interface ScanResult {
    text: string;
    isUrl?: boolean;
}
interface OwnProps {
    extendedStyles?: ScannerStyles;
}

export type ScannerStyles = {
    barcodeIcon?: IconProps;
    barcodeButton?: ButtonPresentationProps;
    mobileBarcodeModal?: TwoButtonModalStyles;
};

const scannerStyles: ScannerStyles = {
    barcodeIcon: {
        color: "secondary",
        size: 15,
    },
    barcodeButton: {
        css: css`
            position: absolute;
            right: 40px;
            z-index: 1;
            top: 50%;
            transform: translateY(-50%);
            padding: 9px;
            background-color: ${color("common.background")};
        `,
    },
    mobileBarcodeModal: {
        container: {
            css: css`
                padding: 0;
            `,
        },
    },
};

type props = OwnProps & HasHistory;

const ProductBarcodeScanner = ({ extendedStyles, history }: props) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [result, setResult] = useState<ScanResult>({ text: "", isUrl: false });
    const [productScannerStyles] = useState(() => mergeWithCss(scannerStyles, extendedStyles));

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleResultChange = (text: string, isUrl?: boolean) => {
        debouncedResultChange(text, isUrl);
    };

    const debouncedResultChange = debounce((text: string, isUrl?: boolean) => {
        setResult({ text, isUrl });
    }, 500);

    const handleError = (errorMessage: any) => {
        logger.error(errorMessage);
    };
     
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setResult({ text: "", isUrl: false });
    };

    const doSearch = () => {
        if (!result || !result.text) {
            return;
        }
        if (result.isUrl) {
            if (typeof window !== "undefined") {
                window.location.href = result.text;
            }
        } else {
            history.push(`/Search?query=${result.text}`);
        }
        handleCloseModal();
    };

    return (
        <>
            <Clickable {...productScannerStyles.barcodeButton} onClick={handleOpenModal}>
                <Icon {...productScannerStyles.barcodeIcon} src={Barcode} />
                <VisuallyHidden>{translate("Search by Barcode")}</VisuallyHidden>
            </Clickable>
            <TwoButtonModal
                headlineText={translate("Search by Barcode")}
                modalIsOpen={isModalOpen}
                messageText={
                    <StyledWrapper>
                        <BarcodeScanner onError={handleError} onChange={handleResultChange} isOpen={isModalOpen} />
                        <Typography variant="h6">{result.text}</Typography>
                    </StyledWrapper>
                }
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Search")}
                onCancel={handleCloseModal}
                onSubmit={doSearch}
                extendedStyles={productScannerStyles.mobileBarcodeModal}
            ></TwoButtonModal>
        </>
    );
};

export default withHistory(ProductBarcodeScanner);
